/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppRoute } from '@agentselly/immodossier-shared';
import { createFileRoute, useLinkProps } from '@tanstack/react-router';
import { useUser, useSignUp, useSignIn, useClerk } from '@clerk/clerk-react';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { OtpInput } from '~/components/OtpInput';
import { LoadingSpinner } from '~/components/global/LoadingSpinner';
import { useGlobalLoader } from '~/context/GlobalLoaderContext';
import {
  isTurnstileError,
  TURNSTILE_USER_MESSAGE,
  TURNSTILE_USER_MESSAGE_KEY,
} from '~/components/auth/turnstileError';
import { T, useTranslate } from '@tolgee/react';
import { useV3Redesign } from '~/helper/useV3Redesign';
import { LegacyRegisterBody } from '~/components/auth/LegacyRegisterBody';

function V3RegisterBody() {
  const { href: dashboardUrl } = useLinkProps({
    to: AppRoute.$LANG_$COMPANYID_INDEX as AppRoute,
  });
  const params = Route.useParams();
  const { t } = useTranslate();
  const { isSignedIn, isLoaded, user } = useUser();
  const clerk = useClerk();
  const { showLoader } = useGlobalLoader();
  const navigate = useNavigate();
  const { signUp, setActive } = useSignUp();
  const { signIn } = useSignIn();

  const emailFromUrl = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('email') || '';
  }, []);

  const [
    email,
    setEmail,
  ] = useState(emailFromUrl);

  // Pre-fill from existing signUp state if available (e.g. from failed OAuth transfer)
  useEffect(() => {
    if (!email && signUp?.emailAddress) {
      console.log(
        'Restoring email from existing signUp session:',
        signUp.emailAddress,
      );
      setEmail(signUp.emailAddress);
    }
  }, [
    signUp,
    email,
  ]);

  const [
    code,
    setCode,
  ] = useState('');
  const [
    agbAccepted,
    setAgbAccepted,
  ] = useState(false);
  const [
    isLoading,
    setIsLoading,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState('');
  const [
    step,
    setStep,
  ] = useState<'email' | 'code'>('email');
  const isVerifyingRef = useRef(false);

  // Dieselbe Meldung erscheint an vier Stellen (E-Mail, Google, Apple,
  // Valiant) — einmal ableiten statt viermal denselben Key aufrufen.
  const legalRequiredMessage = t(
    'auth_register_error_legal_required',
    'Bitte akzeptieren Sie die AGB und Datenschutzrichtlinie.',
  );

  useEffect(() => {
    if (isLoaded && (isSignedIn || user)) {
      console.log('User is signed in, redirecting to dashboard:', {
        isSignedIn,
        user: user?.id,
      });
      showLoader();
      // Route navigation keeps the history stack intact (replace: true because
      // this is a post-login technical redirect).
      void navigate({ to: dashboardUrl as any, replace: true });
    }
  }, [
    isLoaded,
    isSignedIn,
    user,
    showLoader,
    dashboardUrl,
    navigate,
  ]);

  const goToDashboard = useCallback(() => {
    void navigate({ to: dashboardUrl as any, replace: true });
  }, [
    navigate,
    dashboardUrl,
  ]);

  const goToHubSpot = useCallback(
    (hubspotContactId: string) => {
      void navigate({
        to: `/${params.lang}/${params.companyId}/${hubspotContactId}`,
        replace: true,
      });
    },
    [
      navigate,
      params.lang,
      params.companyId,
    ],
  );

  const goToLogin = useCallback(() => {
    void navigate({
      to: `/${params.lang}/${params.companyId}/public/auth/login`,
      replace: true,
    });
  }, [
    navigate,
    params.lang,
    params.companyId,
  ]);

  const verifyCode = useCallback(
    async (codeToVerify: string) => {
      if (isVerifyingRef.current) return;
      isVerifyingRef.current = true;
      setIsLoading(true);
      setError('');

      try {
        const result = await signUp.attemptEmailAddressVerification({
          code: codeToVerify,
        });

        console.log('Verification result:', result.status, result);

        if (result.status === 'complete') {
          if (setActive) {
            await setActive({ session: result.createdSessionId });
          }

          // Get hubspotContactId after successful verification
          const verifiedUser =
            (await clerk.user?.reload?.()) || clerk.user || user;
          const hubspotContactId = (
            verifiedUser?.publicMetadata as Record<string, unknown> | undefined
          )?.hubspotContactId as string | undefined;

          if (hubspotContactId) {
            // Redirect to HubSpot contact page
            setTimeout(() => {
              goToHubSpot(hubspotContactId);
            }, 100);
          } else {
            // Fallback to dashboard
            setTimeout(() => {
              goToDashboard();
            }, 100);
          }
        } else if (result.status === 'missing_requirements') {
          // Check what's missing - might need additional verification
          const missingFields = result.missingFields || [];
          const unverifiedFields = result.unverifiedFields || [];
          console.log(
            'Missing fields:',
            missingFields,
            'Unverified:',
            unverifiedFields,
          );

          // If email is now verified but other requirements exist, try to complete
          if (!unverifiedFields.includes('email_address')) {
            // Email verified, try to create session
            if (result.createdSessionId && setActive) {
              await setActive({ session: result.createdSessionId });

              // Get hubspotContactId after setting session
              const verifiedUser =
                (await clerk.user?.reload?.()) || clerk.user || user;
              const hubspotContactId = (
                verifiedUser?.publicMetadata as
                  | Record<string, unknown>
                  | undefined
              )?.hubspotContactId as string | undefined;

              if (hubspotContactId) {
                // Redirect to HubSpot contact page
                setTimeout(() => {
                  goToHubSpot(hubspotContactId);
                }, 100);
              } else {
                // Fallback to dashboard
                setTimeout(() => {
                  goToDashboard();
                }, 100);
              }
              return;
            }
          }

          setError(
            t(
              'auth_register_error_incomplete',
              'Registrierung unvollständig. Bitte versuchen Sie es erneut.',
            ),
          );
          isVerifyingRef.current = false;
          setIsLoading(false);
        } else {
          console.error('Verification status:', result.status);
          setError(
            t(
              'auth_register_error_verification_incomplete',
              'Verifizierung nicht abgeschlossen. Bitte versuchen Sie es erneut.',
            ),
          );
          isVerifyingRef.current = false;
          setIsLoading(false);
        }
      } catch (err: any) {
        console.error('Code verification error:', err);

        if (err.errors?.[0]?.code === 'form_code_incorrect') {
          setError(
            t(
              'auth_register_error_code_invalid',
              'Der eingegebene Code ist ungültig.',
            ),
          );
        } else if (err.errors?.[0]?.code === 'verification_expired') {
          setError(
            t(
              'auth_register_error_code_expired',
              'Der Code ist abgelaufen. Bitte fordern Sie einen neuen Code an.',
            ),
          );
        } else {
          setError(
            err.errors?.[0]?.message ||
              t('auth_register_error_code_generic', 'Ungültiger Code'),
          );
        }
        isVerifyingRef.current = false;
        setIsLoading(false);
      }
    },
    [
      signUp,
      setActive,
      dashboardUrl,
      goToDashboard,
      goToHubSpot,
      t,
    ],
  );

  const handleGoogleOAuth = useCallback(async () => {
    if (!agbAccepted) {
      setError(legalRequiredMessage);
      return;
    }
    try {
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: dashboardUrl,
        redirectUrlComplete: dashboardUrl,
      });
    } catch (error) {
      console.error('Google OAuth failed:', error);
      setError(
        t(
          'auth_register_error_google_unavailable',
          'Google OAuth ist in der Entwicklungsumgebung nicht verfügbar',
        ),
      );
    }
  }, [
    agbAccepted,
    signUp,
    dashboardUrl,
    legalRequiredMessage,
    t,
  ]);

  const handleAppleOAuth = useCallback(async () => {
    if (!agbAccepted) {
      setError(legalRequiredMessage);
      return;
    }
    try {
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_apple',
        redirectUrl: dashboardUrl,
        redirectUrlComplete: dashboardUrl,
      });
    } catch (error) {
      console.error('Apple OAuth failed:', error);
      setError(
        t(
          'auth_register_error_apple_unavailable',
          'Apple OAuth ist in der Entwicklungsumgebung nicht verfügbar',
        ),
      );
    }
  }, [
    agbAccepted,
    signUp,
    dashboardUrl,
    legalRequiredMessage,
    t,
  ]);

  const handleValiantSSO = useCallback(async () => {
    if (!agbAccepted) {
      setError(legalRequiredMessage);
      return;
    }
    setIsLoading(true);
    setError('');

    try {
      await signIn.authenticateWithRedirect({
        strategy: 'saml',
        identifier: 'sso@valiant.ch',
        redirectUrl: window.location.origin + '/auth/oauth',
        redirectUrlComplete: dashboardUrl,
      } as any);
    } catch (error: any) {
      console.error('Valiant SSO failed:', error);
      setError(
        error.errors?.[0]?.message ||
          t(
            'auth_register_error_valiant_unavailable',
            'Valiant SSO ist nicht verfügbar',
          ),
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    agbAccepted,
    signIn,
    dashboardUrl,
    legalRequiredMessage,
    t,
  ]);

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!agbAccepted) {
        setError(legalRequiredMessage);
        return;
      }

      setIsLoading(true);
      setError('');

      console.log('🚀 STARTING EMAIL REGISTRATION FLOW');
      console.log('Email:', email);
      console.log('AGB accepted:', agbAccepted);
      console.log('SignUp object:', signUp);

      try {
        // If we already have a signUp attempt (e.g. from OAuth transfer), update it instead of creating new
        let result;

        if (
          signUp?.status === 'missing_requirements' ||
          signUp?.status === 'abandoned'
        ) {
          console.log('Updating existing signUp attempt...');
          // Update email if it changed, or just accept terms
          result = await signUp.update({
            emailAddress: email,
            legalAccepted: true,
          });
        } else {
          console.log('Creating new signUp attempt...');
          result = await signUp.create({
            emailAddress: email,
            legalAccepted: true,
          });
        }

        if (result.status === 'missing_requirements') {
          // Check if this is a new account or existing one
          console.log(
            'Missing requirements - checking if new account or existing...',
          );
          console.log('SignUp status before update:', signUp.status);
          console.log('Email being processed:', email);

          // If signUp was just created, this is a new account - proceed with email verification
          if (
            signUp.status === 'created' ||
            signUp.status === null ||
            !signUp.createdUserId
          ) {
            console.log(
              '🚀 NEW ACCOUNT DETECTED - Sending verification code...',
            );
            try {
              await signUp.prepareEmailAddressVerification({
                strategy: 'email_code',
              });
              console.log('✅ Verification code sent successfully');
              setStep('code');
              setIsLoading(false);
              return;
            } catch (verificationError) {
              console.error(
                '❌ Failed to send verification code:',
                verificationError,
              );
              setError(
                t(
                  'auth_register_error_code_not_sent',
                  'Code konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
                ),
              );
              setIsLoading(false);
              return;
            }
          }

          // Otherwise, this is an existing account - try to complete it
          console.log('🔄 EXISTING ACCOUNT DETECTED - Trying to complete...');

          // Try to complete the signUp with terms accepted
          const completeResult = await signUp.update({
            emailAddress: email,
            legalAccepted: true,
          });

          console.log('Complete result:', completeResult.status);

          if (completeResult.status === 'complete') {
            console.log(
              'Account completed successfully, redirecting to HubSpot...',
            );

            // Get hubspotContactId from user metadata after completing
            const completedUser = signUp.createdUserId
              ? (await clerk.user?.reload?.()) || clerk.user || user
              : clerk.user || user;
            const hubspotContactId = (
              completedUser?.publicMetadata as
                | Record<string, unknown>
                | undefined
            )?.hubspotContactId as string | undefined;

            if (hubspotContactId) {
              // Redirect to HubSpot contact page
              goToHubSpot(hubspotContactId);
            } else {
              // Fallback to dashboard
              goToDashboard();
            }
          } else {
            // Still missing requirements, try email verification
            await signUp.prepareEmailAddressVerification({
              strategy: 'email_code',
            });
            setStep('code');
          }
        } else {
          // New account creation
          console.log('Creating new signUp attempt...');
          result = await signUp.create({
            emailAddress: email,
            legalAccepted: true,
          });

          if (result.status === 'complete') {
            console.log(
              'New account created successfully, redirecting to HubSpot...',
            );

            // Get hubspotContactId from newly created user
            const newUser =
              (await clerk.user?.reload?.()) || clerk.user || user;
            const hubspotContactId = (
              newUser?.publicMetadata as Record<string, unknown> | undefined
            )?.hubspotContactId as string | undefined;

            if (hubspotContactId) {
              // Redirect to HubSpot contact page
              goToHubSpot(hubspotContactId);
            } else {
              // Fallback to dashboard
              goToDashboard();
            }
          } else if (result.status === 'missing_requirements') {
            await signUp.prepareEmailAddressVerification({
              strategy: 'email_code',
            });
            setStep('code');
          } else if (result.status !== 'complete') {
            setError(
              t(
                'auth_register_error_generic',
                'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
              ),
            );
          }
        }
      } catch (err: any) {
        console.error('Registration error:', err);

        if (err.errors?.[0]?.code === 'form_identifier_exists') {
          console.log('Email already exists, redirecting to login...');
          // Email already exists, redirect to login page instead of showing error
          goToLogin();
        } else if (isTurnstileError(err)) {
          setError(t(TURNSTILE_USER_MESSAGE_KEY, TURNSTILE_USER_MESSAGE));
        } else {
          setError(
            err.errors?.[0]?.message ||
              t('auth_register_error_failed', 'Fehler bei der Registrierung'),
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      agbAccepted,
      email,
      signUp,
      goToDashboard,
      goToHubSpot,
      goToLogin,
      legalRequiredMessage,
      t,
    ],
  );

  const handleCodeSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (code.length === 6 && !isVerifyingRef.current) {
        await verifyCode(code);
      }
    },
    [
      code,
      verifyCode,
    ],
  );

  const goBack = useCallback(() => {
    setStep('email');
    setCode('');
    setError('');
    isVerifyingRef.current = false;
  }, []);

  if (!isLoaded) {
    console.log('Register page: Clerk not loaded yet');
    return <LoadingSpinner mainLoader />;
  }

  console.log('Register page state:', {
    isLoaded,
    isSignedIn,
    user: user?.id,
    userEmail: user?.primaryEmailAddress?.emailAddress,
    signUpStatus: signUp?.status,
    dashboardUrl,
  });

  const renderProviderIcon = (providerName: string) => {
    switch (providerName) {
      case 'google':
        return (
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
          >
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
        );
      case 'apple':
        return (
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
          >
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.35-1.09-.56-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
        );
      case 'valiant':
        return (
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <text
              x="50%"
              y="50%"
              dominantBaseline="central"
              textAnchor="middle"
              fontSize="14"
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
            >
              V
            </text>
          </svg>
        );
      default:
        return (
          <svg
            className="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
        );
    }
  };

  return (
    <div className="flex items-start justify-center py-8 px-4 min-h-full">
      <div className="max-w-md w-full">
        <div className="rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 shadow-sm dark:shadow-none p-6 sm:p-8 space-y-6 overflow-visible">
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
              {t('auth_register_title', 'Konto erstellen')}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {step === 'email'
                ? t('auth_register_subtitle_email', 'Erstellen Sie Ihr Konto')
                : t(
                    'auth_register_subtitle_code',
                    'Geben Sie den Verifizierungscode ein',
                  )}
            </p>
          </div>

          <div className="flex gap-2">
            <div
              className={`flex-1 py-2 px-3 rounded-lg text-xs flex items-center gap-2 transition-colors ${
                step === 'email'
                  ? 'bg-gray-100 dark:bg-zinc-800 text-gray-900 dark:text-gray-100 font-semibold'
                  : 'bg-gray-50 dark:bg-zinc-900 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-zinc-800'
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[11px] font-bold ${
                  step === 'email'
                    ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-950'
                    : 'bg-gray-200 dark:bg-zinc-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                1
              </span>
              <span>{t('auth_register_step_1', 'Angaben')}</span>
            </div>
            <div
              className={`flex-1 py-2 px-3 rounded-lg text-xs flex items-center gap-2 transition-colors ${
                step === 'code'
                  ? 'bg-gray-100 dark:bg-zinc-800 text-gray-900 dark:text-gray-100 font-semibold'
                  : 'bg-gray-50 dark:bg-zinc-900 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-zinc-800'
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[11px] font-bold ${
                  step === 'code'
                    ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-950'
                    : 'bg-gray-200 dark:bg-zinc-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                2
              </span>
              <span>{t('auth_register_step_2', 'Bestätigung')}</span>
            </div>
          </div>

          {step === 'email' ? (
            <>
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={handleGoogleOAuth}
                  disabled={!agbAccepted}
                  className="flex-1 min-h-[44px] flex items-center justify-center px-2.5 sm:px-4 py-2.5 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg text-gray-900 dark:text-gray-100 text-xs sm:text-sm font-semibold hover:bg-gray-50 dark:hover:bg-zinc-800 hover:border-gray-300 dark:hover:border-zinc-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 group disabled:opacity-50 disabled:cursor-not-allowed shadow-sm dark:shadow-none"
                >
                  <span className="mr-1.5 sm:mr-2 group-hover:scale-110 transition-transform duration-200">
                    {renderProviderIcon('google')}
                  </span>
                  <span className="font-semibold">
                    {t('auth_provider_google', 'Google')}
                  </span>
                </button>

                <button
                  onClick={handleAppleOAuth}
                  disabled={!agbAccepted}
                  className="flex-1 min-h-[44px] flex items-center justify-center px-2.5 sm:px-4 py-2.5 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg text-gray-900 dark:text-gray-100 text-xs sm:text-sm font-semibold hover:bg-gray-50 dark:hover:bg-zinc-800 hover:border-gray-300 dark:hover:border-zinc-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 group disabled:opacity-50 disabled:cursor-not-allowed shadow-sm dark:shadow-none"
                >
                  <span className="mr-1.5 sm:mr-2 group-hover:scale-110 transition-transform duration-200">
                    {renderProviderIcon('apple')}
                  </span>
                  <span className="font-semibold">
                    {t('auth_provider_apple', 'Apple')}
                  </span>
                </button>

                <button
                  onClick={handleValiantSSO}
                  disabled={!agbAccepted}
                  // eslint-disable-next-line no-restricted-syntax
                  className="flex-1 min-h-[44px] flex items-center justify-center px-2.5 sm:px-4 py-2.5 bg-[#7252AC] hover:bg-[#5B418B] border border-[#7252AC] rounded-lg text-white text-xs sm:text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7252AC] group disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  <span className="mr-1.5 sm:mr-2 group-hover:scale-110 transition-transform duration-200">
                    {renderProviderIcon('valiant')}
                  </span>
                  <span className="font-semibold">
                    {t('auth_provider_valiant', 'Valiant')}
                  </span>
                </button>
              </div>

              <div className="relative flex items-center justify-center my-1">
                <div className="w-full border-t border-gray-200 dark:border-zinc-800"></div>
                <span className="absolute px-3 bg-white dark:bg-zinc-900 text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium">
                  {t('auth_divider_or', 'oder')}
                </span>
              </div>

              <div className="space-y-4">
                <h3 className="text-center text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t(
                    'auth_register_email_code_title',
                    'Mit E-Mail Code registrieren',
                  )}
                </h3>

                <form
                  onSubmit={handleEmailSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"
                    >
                      {t('auth_email_label', 'E-Mail-Adresse')}
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="min-h-[44px] block w-full px-3.5 py-2.5 border border-gray-200 dark:border-zinc-800 rounded-lg bg-white dark:bg-zinc-900 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 transition-colors"
                      placeholder={t(
                        'auth_register_email_placeholder',
                        'ihre@email.com',
                      )}
                    />
                  </div>

                  <div className="flex items-start">
                    <input
                      id="agb"
                      type="checkbox"
                      checked={agbAccepted}
                      onChange={(e) => setAgbAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-gray-900 dark:focus:ring-white dark:bg-zinc-900"
                    />
                    <label
                      htmlFor="agb"
                      className="ml-2.5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed"
                    >
                      <T
                        keyName="auth_register_legal_consent"
                        defaultValue="Ich akzeptiere die <terms>AGB</terms> und die <privacy>Datenschutzrichtlinie</privacy>"
                        params={{
                          terms: (
                            <a
                              href="https://www.agentselly.ch/agb/"
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary-500 hover:text-primary-400 font-medium underline"
                            />
                          ),
                          privacy: (
                            <a
                              href="https://www.agentselly.ch/datenschutz/"
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary-500 hover:text-primary-400 font-medium underline"
                            />
                          ),
                        }}
                      />
                    </label>
                  </div>

                  {error && (
                    <div className="text-red-600 dark:text-red-400 text-sm text-center p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-900/50">
                      {error}
                    </div>
                  )}

                  {/* Clerk CAPTCHA container for bot protection */}
                  <div id="clerk-captcha" />

                  <button
                    type="submit"
                    disabled={isLoading || !email || !agbAccepted}
                    className="w-full min-h-[44px] flex items-center justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white dark:text-gray-950 bg-gray-950 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm dark:shadow-none"
                  >
                    {isLoading
                      ? t('auth_sending_code', 'Code wird gesendet...')
                      : t('auth_register_request_code', 'Code senden')}
                  </button>
                </form>
              </div>
            </>
          ) : (
            <>
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('auth_register_verify_title', 'E-Mail verifizieren')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  <T
                    keyName="auth_code_sent_to"
                    defaultValue="Wir haben einen Verifizierungscode an <strong>{email}</strong> gesendet"
                    params={{
                      email,
                      strong: <strong />,
                    }}
                  />
                </p>
              </div>

              <form
                onSubmit={handleCodeSubmit}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 text-center">
                    {t('auth_code_label', 'Verifizierungscode')}
                  </label>
                  <OtpInput
                    value={code}
                    onChange={setCode}
                    onComplete={verifyCode}
                    length={6}
                    disabled={isLoading}
                  />
                </div>

                {error && (
                  <div className="text-red-600 dark:text-red-400 text-sm text-center p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-900/50">
                    {error}
                  </div>
                )}

                <div className="flex gap-3 w-full">
                  <button
                    type="button"
                    onClick={goBack}
                    className="flex-1 min-h-[44px] flex items-center justify-center py-2.5 px-4 border border-gray-200 dark:border-zinc-800 rounded-lg text-sm font-semibold text-gray-900 dark:text-gray-100 bg-white dark:bg-zinc-900 hover:bg-gray-50 dark:hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900 transition-colors shadow-sm dark:shadow-none"
                  >
                    {t('auth_back', 'Zurück')}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || code.length !== 6}
                    className="flex-1 min-h-[44px] flex items-center justify-center py-2.5 px-4 rounded-lg text-sm font-semibold text-white dark:text-gray-950 bg-gray-950 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm dark:shadow-none"
                  >
                    {t('auth_register_verify_submit', 'Verifizieren')}
                  </button>
                </div>
              </form>

              <div className="text-center mt-4">
                <button
                  onClick={goBack}
                  className="min-h-[44px] inline-flex items-center justify-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-medium transition-colors"
                >
                  {t('auth_resend_code', 'Code erneut senden')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_PUBLIC_AUTH_REGISTER as any,
)({
  component: function Entry() {
    const v3Redesign = useV3Redesign();
    const params = Route.useParams();
    return v3Redesign ? (
      <V3RegisterBody />
    ) : (
      <LegacyRegisterBody params={params} />
    );
  },
});
